import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { api } from "../api";

// ─── Terms Modal ──────────────────────────────────────────────────────────────
function TermsModal({ onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div style={{ background: "#fff", borderRadius: 10, width: "100%", maxWidth: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid var(--border)" }}>
          <strong style={{ fontSize: "1rem" }}>Privacy Policy &amp; Terms of Service</strong>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: "1.2rem", cursor: "pointer", color: "var(--text-muted)", lineHeight: 1 }}>✕</button>
        </div>
        <div style={{ overflowY: "auto", padding: "20px 24px", fontSize: "0.875rem", lineHeight: 1.75, color: "var(--text)" }}>
          <h3 style={{ fontSize: "0.95rem", marginBottom: 8 }}>Privacy Policy – Use of Email Address</h3>
          <p style={{ marginBottom: 16, color: "var(--text-muted)" }}>
            To provide you with secure and personalized access to the CowTown Redi Mix Customer Portal, we collect your email address during account setup. Here's how we use and protect this information.
          </p>

          <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6 }}>1. Why We Collect Your Information</h4>
          <ul style={{ paddingLeft: 20, marginBottom: 16, color: "var(--text-muted)" }}>
            <li style={{ marginBottom: 4 }}><strong>Email Address:</strong> Used for account creation, login, password resets, and support communication.</li>
          </ul>

          <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6 }}>2. How We Use Your Information</h4>
          <ul style={{ paddingLeft: 20, marginBottom: 16, color: "var(--text-muted)" }}>
            <li style={{ marginBottom: 4 }}>To verify your identity and grant portal access.</li>
            <li style={{ marginBottom: 4 }}>To communicate important account updates or service information.</li>
            <li style={{ marginBottom: 4 }}>To resolve support issues efficiently.</li>
          </ul>

          <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6 }}>3. Your Privacy Matters</h4>
          <ul style={{ paddingLeft: 20, marginBottom: 16, color: "var(--text-muted)" }}>
            <li style={{ marginBottom: 4 }}>We do not sell or rent your personal information to third parties.</li>
            <li style={{ marginBottom: 4 }}>Your contact details are stored securely and only accessible to authorized CowTown Redi Mix personnel.</li>
            <li style={{ marginBottom: 4 }}>You can request deletion of your account at any time by contacting our support team.</li>
          </ul>

          <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6 }}>4. Terms of Service</h4>
          <p style={{ color: "var(--text-muted)", marginBottom: 16 }}>
            Access to this portal is provided exclusively to active CowTown Redi Mix customers. Unauthorized access, sharing of login credentials, or misuse of this portal is prohibited. CowTown Redi Mix reserves the right to suspend or revoke access at any time.
          </p>

          <h4 style={{ fontSize: "0.85rem", fontWeight: 700, marginBottom: 6 }}>5. Consent</h4>
          <p style={{ color: "var(--text-muted)" }}>
            By signing in, you consent to our use of your information as outlined in this policy. For questions, contact us at <a href="mailto:ssmith@cowtownrm.us" style={{ color: "var(--blue)" }}>ssmith@cowtownrm.us</a> or call <a href="tel:8177591919" style={{ color: "var(--blue)" }}>817-759-1919</a>.
          </p>
        </div>
        <div style={{ padding: "14px 24px", borderTop: "1px solid var(--border)", textAlign: "right" }}>
          <button onClick={onClose} className="btn btn-primary">Close</button>
        </div>
      </div>
    </div>
  );
}

// ─── Left panel content ───────────────────────────────────────────────────────
function AuthLeftPanel({ tagline }) {
  return (
    <div className="auth-panel-left">
      <div className="auth-brand">
        <img src="/logo.png" alt="Cow Town Redi Mix" />
        <div className="auth-brand-name">Customer Portal</div>
        <div className="auth-brand-tagline">{tagline || "View your invoices, track balances, and manage your account."}</div>

      </div>
    </div>
  );
}

// ─── Login ────────────────────────────────────────────────────────────────────
export function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const data = await api.login({ email, password });
      login(data.token, data.email);
      navigate("/");
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      {showTerms && <TermsModal onClose={() => setShowTerms(false)} />}
      <div className="auth-shell">
        <AuthLeftPanel />
        <div className="auth-panel-right">
          <div className="auth-form-wrap">
            <div className="auth-form-title">Welcome back.</div>
            <div className="auth-form-sub">Sign in to your account portal.</div>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
              </div>
              <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} type="submit" disabled={loading}>
                {loading ? "Signing in..." : "Sign In"}
              </button>
            </form>

            <div className="auth-switch">
              <Link to="/forgot-password">Forgot password?</Link>
            </div>

            {/* Terms line */}
            <div style={{ marginTop: 20, fontSize: "0.78rem", color: "var(--text-muted)", textAlign: "center", lineHeight: 1.6 }}>
              By signing in, you agree to our{" "}
              <button onClick={() => setShowTerms(true)} style={{ background: "none", border: "none", color: "var(--blue)", cursor: "pointer", fontSize: "inherit", padding: 0, textDecoration: "underline" }}>
                Terms of Service &amp; Privacy Policy
              </button>
            </div>

            {/* Need Help */}
            <div style={{ marginTop: 20, background: "var(--bg)", borderRadius: "var(--radius)", padding: "14px 16px", fontSize: "0.82rem", color: "var(--text-muted)", lineHeight: 1.7 }}>
              <div style={{ fontWeight: 600, color: "var(--text)", marginBottom: 4 }}>Need Help?</div>
              <div>Call us at <a href="tel:8177591919" style={{ color: "var(--blue)" }}>817-759-1919 x403</a></div>
              <div>Email <a href="mailto:salem@cowtownrm.us" style={{ color: "var(--blue)" }}>salem@cowtownrm.us</a></div>
              <div style={{ marginTop: 4, fontSize: "0.78rem" }}>
                Don't have an account? Contact us to request access.
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Register (invite-only) ───────────────────────────────────────────────────
export function RegisterPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();

  const [inviteInfo, setInviteInfo] = useState(null);
  const [inviteError, setInviteError] = useState("");
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    api.verifyInvite(token)
      .then(data => { setInviteInfo(data); setLoading(false); })
      .catch(err => { setInviteError(err.message); setLoading(false); });
  }, [token]);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setError(""); setSubmitting(true);
    try {
      await api.register({ token, password });
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <div className="auth-shell">
        <AuthLeftPanel tagline="Access by invitation only." />
        <div className="auth-panel-right">
          <div className="auth-form-wrap">
            <div className="auth-form-title">Request Access</div>
            <div className="auth-form-sub">Portal access is by invitation only. Contact us to get set up.</div>
            <div style={{ background: "var(--bg)", borderRadius: "var(--radius)", padding: "20px", marginBottom: 24, fontSize: "0.9rem", lineHeight: 1.8 }}>
              <div><strong>Office:</strong> <a href="tel:8177591919" style={{ color: "var(--blue)" }}>817-759-1919</a></div>
              <div><strong>Dispatch:</strong> <a href="tel:8177592599" style={{ color: "var(--blue)" }}>817-759-2599</a></div>
              <div><strong>Website:</strong> <a href="https://cowtownrm.com" target="_blank" rel="noreferrer" style={{ color: "var(--blue)" }}>cowtownrm.com</a></div>
            </div>
            <Link to="/login" className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}>Back to Sign In</Link>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return (
    <div className="auth-shell">
      <AuthLeftPanel />
      <div className="auth-panel-right"><div className="auth-form-wrap"><div className="auth-form-sub">Verifying invite...</div></div></div>
    </div>
  );

  if (inviteError) return (
    <div className="auth-shell">
      <AuthLeftPanel />
      <div className="auth-panel-right">
        <div className="auth-form-wrap">
          <div className="auth-form-title">Invalid Invite</div>
          <div className="alert alert-error">{inviteError}</div>
          <Link to="/login" className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}>Back to Sign In</Link>
        </div>
      </div>
    </div>
  );

  if (success) return (
    <div className="auth-shell">
      <AuthLeftPanel />
      <div className="auth-panel-right">
        <div className="auth-form-wrap">
          <div className="auth-form-title">Account Created!</div>
          <div className="auth-form-sub">Your account for <strong>{inviteInfo?.customer_name || inviteInfo?.company_name}</strong> has been set up.</div>
          <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} onClick={() => navigate("/login")}>
            Sign In Now
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="auth-shell">
      <AuthLeftPanel tagline="Set up your account to get started." />
      <div className="auth-panel-right">
        <div className="auth-form-wrap">
          <div className="auth-form-title">Set Up Your Account</div>
          <div className="auth-form-sub">
            {(inviteInfo?.customer_name || inviteInfo?.company_name) && <><strong>{inviteInfo.customer_name || inviteInfo.company_name}</strong><br /></>}
            {inviteInfo?.email}
          </div>
          {error && <div className="alert alert-error">{error}</div>}
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Create Password</label>
              <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} autoFocus placeholder="Minimum 8 characters" />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <input className="form-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} />
            </div>
            <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} type="submit" disabled={submitting}>
              {submitting ? "Creating Account..." : "Create Account"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

// ─── Forgot Password ──────────────────────────────────────────────────────────
export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.forgotPassword({ email });
      setSent(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-shell">
      <AuthLeftPanel />
      <div className="auth-panel-right">
        <div className="auth-form-wrap">
          <div className="auth-form-title">Reset Password</div>
          {sent ? (
            <>
              <div className="auth-form-sub">If an account exists for that email, a reset link has been sent.</div>
              <Link to="/login" className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}>Back to Sign In</Link>
            </>
          ) : (
            <>
              <div className="auth-form-sub">Enter your email and we'll send you a reset link.</div>
              {error && <div className="alert alert-error">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">Email Address</label>
                  <input className="form-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
                </div>
                <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} type="submit" disabled={loading}>
                  {loading ? "Sending..." : "Send Reset Link"}
                </button>
              </form>
              <div className="auth-switch"><Link to="/login">Back to Sign In</Link></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Reset Password ───────────────────────────────────────────────────────────
export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    if (password !== confirm) { setError("Passwords do not match."); return; }
    setError(""); setLoading(true);
    try {
      await api.resetPassword({ token, password });
      setSuccess(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  if (!token) return (
    <div className="auth-shell">
      <AuthLeftPanel />
      <div className="auth-panel-right"><div className="auth-form-wrap"><div className="alert alert-error">Invalid reset link.</div></div></div>
    </div>
  );

  return (
    <div className="auth-shell">
      <AuthLeftPanel />
      <div className="auth-panel-right">
        <div className="auth-form-wrap">
          <div className="auth-form-title">Set New Password</div>
          {success ? (
            <div className="alert alert-success">Password updated! Redirecting to sign in...</div>
          ) : (
            <>
              {error && <div className="alert alert-error">{error}</div>}
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input className="form-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required minLength={8} autoFocus />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input className="form-input" type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required minLength={8} />
                </div>
                <button className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }} type="submit" disabled={loading}>
                  {loading ? "Updating..." : "Update Password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
